import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';

import { IUser } from '../../UserSlice';
import { RootState } from '../..';
import {
  adminCreateStudentFlexibleRateVersion,
  adminDeleteStudentFlexibleRateVersion,
  adminGetStudentFlexibleRateVersions,
  adminGetStudentFlexibleRateVersionsForDropDown,
  adminUpdateStudentFlexibleRateVersion,
  adminVerifyStudentFlexibleRateVersionDelete,
} from './adminApi';

export interface IStudentFlexibleRateVersion {
  _id: string;
  name: string;
  createdBy: string | IUser;
  updatedBy: string | IUser;
}

export interface IStudentFlexibleRateVersionDeleteVerificationData {
  studentFlexibleRateCount: number;
}

export interface IInitialStudentFlexibleRateVersionsState {
  status: 'idle' | 'loading' | 'rejected';
  // Admin
  adminStudentFlexibleRateVersionListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IStudentFlexibleRateVersion>;
  };
  adminStudentFlexibleRateVersionListDataForDropDown?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IStudentFlexibleRateVersion>;
  };
  adminStudentFlexibleRateVersionDeleteVerificationData?: IStudentFlexibleRateVersionDeleteVerificationData;
}

const initialState: IInitialStudentFlexibleRateVersionsState = {
  status: 'idle',
  adminStudentFlexibleRateVersionListData: undefined,
  adminStudentFlexibleRateVersionListDataForDropDown: undefined,
  adminStudentFlexibleRateVersionDeleteVerificationData: undefined,
};

export const StudentFlexibleRateVersionsSlice = createSlice({
  initialState,
  name: 'studentFlexibleRateVersions',
  reducers: {
    resetAdminStudentFlexibleRateVersionListData: (state) => {
      state.adminStudentFlexibleRateVersionListData = undefined;
    },
    resetAdminStudentFlexibleRateVersionListDataForDropDown: (state) => {
      state.adminStudentFlexibleRateVersionListDataForDropDown = undefined;
    },
    resetAdminStudentFlexibleRateVersionDeleteVerificationData: (state) => {
      state.adminStudentFlexibleRateVersionDeleteVerificationData = undefined;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(
        adminGetStudentFlexibleRateVersions.fulfilled,
        (state, action) => {
          const result = action.payload;

          const newAdminStudentFlexibleRateVersionListData = {
            limit: result.limit,
            offset: result.offset,
            totalDocs: result.totalDocs || 0,
            list: result.docs || [],
          };

          state.adminStudentFlexibleRateVersionListData = newAdminStudentFlexibleRateVersionListData;
        }
      )
      .addCase(
        adminGetStudentFlexibleRateVersionsForDropDown.fulfilled,
        (state, action) => {
          const result = action.payload;

          const newAdminStudentFlexibleRateVersionListDataForDropDown = {
            limit: result.limit,
            offset: result.offset,
            totalDocs: result.totalDocs || 0,
            list: result.docs || [],
          };

          state.adminStudentFlexibleRateVersionListDataForDropDown = newAdminStudentFlexibleRateVersionListDataForDropDown;
        }
      )
      .addCase(
        adminCreateStudentFlexibleRateVersion.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (state.adminStudentFlexibleRateVersionListData) {
            const newAdminStudentFlexibleRateVersionListData = {
              ...state.adminStudentFlexibleRateVersionListData,
            };

            newAdminStudentFlexibleRateVersionListData.list.unshift(result);

            if (newAdminStudentFlexibleRateVersionListData.totalDocs) {
              newAdminStudentFlexibleRateVersionListData.totalDocs += 1;
            } else {
              newAdminStudentFlexibleRateVersionListData.totalDocs = 1;
            }

            state.adminStudentFlexibleRateVersionListData = newAdminStudentFlexibleRateVersionListData;
          }
        }
      )
      .addCase(
        adminUpdateStudentFlexibleRateVersion.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (
            state.adminStudentFlexibleRateVersionListData &&
            state.adminStudentFlexibleRateVersionListData.list &&
            state.adminStudentFlexibleRateVersionListData.list.length
          ) {
            const newAdminStudentFlexibleRateVersionListData = {
              ...state.adminStudentFlexibleRateVersionListData,
            };

            const index = newAdminStudentFlexibleRateVersionListData.list.findIndex(
              (item) => item._id === result._id
            );

            if (index > -1) {
              newAdminStudentFlexibleRateVersionListData.list[index] = result;
            }

            state.adminStudentFlexibleRateVersionListData = newAdminStudentFlexibleRateVersionListData;
          }
        }
      )
      .addCase(
        adminDeleteStudentFlexibleRateVersion.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (
            state.adminStudentFlexibleRateVersionListData &&
            state.adminStudentFlexibleRateVersionListData.list &&
            state.adminStudentFlexibleRateVersionListData.list.length
          ) {
            const newAdminStudentFlexibleRateVersionListData = {
              ...state.adminStudentFlexibleRateVersionListData,
            };

            newAdminStudentFlexibleRateVersionListData.totalDocs = newAdminStudentFlexibleRateVersionListData.totalDocs
              ? newAdminStudentFlexibleRateVersionListData.totalDocs - 1
              : 0;

            newAdminStudentFlexibleRateVersionListData.list = newAdminStudentFlexibleRateVersionListData.list.filter(
              (item) => item._id !== result._id
            );

            state.adminStudentFlexibleRateVersionListData = newAdminStudentFlexibleRateVersionListData;
          }
        }
      )
      .addCase(
        adminVerifyStudentFlexibleRateVersionDelete.fulfilled,
        (state, action) => {
          const result = action.payload;

          state.adminStudentFlexibleRateVersionDeleteVerificationData = result;
        }
      );

    // Common Matchers
    builder.addMatcher(
      isPending(
        adminCreateStudentFlexibleRateVersion,
        adminGetStudentFlexibleRateVersions,
        adminUpdateStudentFlexibleRateVersion,
        adminDeleteStudentFlexibleRateVersion,
        adminVerifyStudentFlexibleRateVersionDelete
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isFulfilled(
        adminCreateStudentFlexibleRateVersion,
        adminGetStudentFlexibleRateVersions,
        adminUpdateStudentFlexibleRateVersion,
        adminDeleteStudentFlexibleRateVersion,
        adminVerifyStudentFlexibleRateVersionDelete
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isRejected(
        adminCreateStudentFlexibleRateVersion,
        adminGetStudentFlexibleRateVersions,
        adminUpdateStudentFlexibleRateVersion,
        adminDeleteStudentFlexibleRateVersion,
        adminVerifyStudentFlexibleRateVersionDelete
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getStudentFlexibleRateVersionsState = (
  state: RootState
): RootState['studentFlexibleRateVersions'] =>
  state.studentFlexibleRateVersions;

export const {
  resetAdminStudentFlexibleRateVersionListData,
  resetAdminStudentFlexibleRateVersionListDataForDropDown,
  resetAdminStudentFlexibleRateVersionDeleteVerificationData,
} = StudentFlexibleRateVersionsSlice.actions;

export * from './adminApi';
export default StudentFlexibleRateVersionsSlice.reducer;
